<template>
    <Section
        v-if="document.data.test_score"
        class="lender-test bg-blue"
        width="md"
    >
        <h2>{{ $prismic.asText(document.data.test_h2) }}</h2>
        <p> {{ document.data.test_p }} </p>

        <div class="container">
            <div class="container-inner">
                <div
                    class="rich-text rich-text-white"
                    v-html="$prismic.asHtml(document.data.test_review)"
                />
                <div v-if="compareBrokerPages.length" class="compare-two-brokers-wrapper">
                    <h3>
                        {{ $translate('lp_our_comparison_h2', 'Våra jämförelser') }}
                    </h3>
                    <div class="compare-two-brokers-links">
                        <nuxt-link 
                            v-for="page in compareBrokerPages" 
                            :key="page.id"
                            :to="$prismic.linkResolver(page)"
                        >
                            {{ page.data.title }}
                        </nuxt-link>
                    </div>
                </div>
            </div>

            <div class="container-inner about-test">
                <div class="about-test-header">
                    <PrismicImage
                        v-if="document.data.icon.url"
                        :img="document.data.icon"
                        class="lender-logo"
                        w="40"
                        h="40"
                    />
                    <h3>
                        {{ $translate('lt_about_the_test_h3', 'Om testet') }}
                    </h3>
                </div>

                <ul>
                    <li v-for="(item, index) in testData" :key="index">
                        <span>{{ item.label }}</span>
                        <span v-html="item.value" />
                    </li>
                </ul>
                <div class="author-container">
                    <TestPerformedByAuthor :document-author="document.data.author" />
                </div>
            </div>
        </div>
    </Section>
</template>

<script>
import TestPerformedByAuthor from '@/components/TestPerformedByAuthor.vue';
import { queryAll, replace } from '@swegaming-ab/nuxtjs-helpers';
export default {
    components: { TestPerformedByAuthor },
    props: {
        document: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            compareBrokerPages: []
        };
    },
    async fetch() {
        if (this.$validateText(this.comparisonTag)) {
            this.compareBrokerPages = replace(await queryAll(this.$prismic.api, 
                [
                    this.$prismic.predicates.at('document.type', 'page'),
                    this.$prismic.predicates.at('document.tags', [this.comparisonTag]),
                ],
                { 
                    orderings: '[document.first_publication_date desc]', 
                    lang: process.env.PRISMIC_LOCALE 
                }
            ));
        }
    },
    fetchKey() {
        return `compare-brokers-${this.document.id}`;
    },
    computed: {
        comparisonTag() {
            return this.document.data.comparison_tag;
        },
        testData() {
            return [
                {
                    label: this.$translate('lt_test_date_label', 'Testet utfördes'),
                    value: this.document.data.test_date
                }, 
                {
                    label: this.$translate('lt_loan_amount_label', 'Lånebelopp'),
                    value: this.$format.currency(this.document.data.test_amount)
                }, 
                {
                    label: this.$translate('lt_duration_label', 'Vald löptid'),
                    value: this.document.data.test_loan_duration
                }, 
                {
                    label: this.$translate('lt_payout_time_label', 'Utbetalningstid'),
                    value: this.document.data.test_payout_time,
                    disabled: !this.$validateText(this.document.data.test_payout_time)
                }, 
                {
                    label: this.$translate('lt_about_the_borrower_label', 'Om låntagaren'),
                    value: this.document.data.test_about_tester
                },
                {
                    label: this.$translate('lt_review_score_label', 'Vårt omdöme'),
                    value: `<span class="our-review-score"><i class="fa-solid fa-star"></i> ${this.document.data.test_score} / 5</span>`
                }
            ].filter(item => !item.disabled);
        },
        author() {
            const hasAuthor = this.$validateContentRelationship(this.document.data.author);

            return hasAuthor
                ? this.$store.state.authors[this.document.data.author.id]
                : null;
        },
        testPerformedBy() {
            return this.$translate('lt_test_performed_by', 'Test utfört av {author}')
                .replace('{author}', `<span class="editor-name">${this.author.data.name}</span>`);
        }
    }
};
</script>

<style lang="scss" scoped>
.lender-test::v-deep {
    .our-review-score {
        font-weight: bold;
    }
}

h2,p {
    color: $white;
    width: 100%;
    text-align: center;
}

p {
    margin-top: 30px;
    padding-bottom: 20px;
    margin-bottom: 20px;
    @include border(bottom,2px,rgba(255,255,255,.25));

}

.container {
    color: $white;
    width: 100%;
    @include grid(1, 40px);

    @include device(desktop) {
        @include grid(2, 40px);
    }

    &-inner:first-child {
        order: 6;
        @include device(desktop) {
            order: 0;
        }
    }

    &-inner:last-child {
        ul {
            @include grid(1, 20px);

            li {
                @include grid(2, 20px);
                width: 100%;

                span:first-child {font-family: $fontHeader;}
            }
        }

        .about-test-header {
            display: flex;
            gap: .75rem;
            img {
                @include border-radius(5px);
            }

            h3 { 
                margin-bottom: 30px; 
            }
        }
        .author-container {
            @include border(top,2px,rgba(255,255,255,.25));
            @include spacing(margin, 3, top);
            @include spacing(padding, 3, top);
            width: 100%;
        }
    }

    .compare-two-brokers-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .compare-two-brokers-links {
            display: flex;
            flex-direction: column;

            a {
                color: $white;
                text-decoration: underline;
                font-weight: bold;
                padding: .5rem 0;
                &:hover {
                    text-decoration: none;
                }
            }
        }

    }
}

</style>
